import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "home-page" }
const _hoisted_2 = { class: "featured-section" }
const _hoisted_3 = { class: "section-container" }
const _hoisted_4 = { class: "section-header" }
const _hoisted_5 = { class: "view-toggle-wrap" }
const _hoisted_6 = { class: "view-toggle" }
const _hoisted_7 = { class: "categories-section" }
const _hoisted_8 = { class: "section-container" }
const _hoisted_9 = { class: "section-header" }
const _hoisted_10 = {
  key: 0,
  class: "categories-scroll"
}
const _hoisted_11 = {
  key: 1,
  class: "categories-scroll"
}
const _hoisted_12 = { class: "category-icon" }
const _hoisted_13 = { class: "category-name" }
const _hoisted_14 = {
  key: 0,
  class: "category-desc"
}
const _hoisted_15 = { class: "category-count" }
const _hoisted_16 = { class: "stats-section" }
const _hoisted_17 = { class: "section-container" }
const _hoisted_18 = { class: "stats-grid" }
const _hoisted_19 = { class: "stat-item" }
const _hoisted_20 = {
  key: 0,
  class: "stat-value-skeleton"
}
const _hoisted_21 = {
  key: 1,
  class: "stat-value"
}
const _hoisted_22 = { class: "stat-item" }
const _hoisted_23 = {
  key: 0,
  class: "stat-value-skeleton"
}
const _hoisted_24 = {
  key: 1,
  class: "stat-value"
}
const _hoisted_25 = { class: "stat-item" }
const _hoisted_26 = {
  key: 0,
  class: "stat-value-skeleton"
}
const _hoisted_27 = {
  key: 1,
  class: "stat-value"
}

import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';
import { userService } from '../firebase/services/userService';
import { useAuth } from '../composables/useAuth';
import { useCategoryStore } from '../stores/categoryStore';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import HeroSection from '../components/HeroSection.vue';
import SmartAd from '../components/SmartAd.vue';

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'Home',
  setup(__props) {

const featuredWallpapers = ref<Wallpaper[]>([]);
const loading = ref(false);
const userFavorites = ref<string[]>([]);
const viewMode = ref<'grid' | 'preview'>('grid');
const categoryMap = ref<Record<string, string>>({});

const { currentUser } = useAuth();

// Initialize stores
const categoryStore = useCategoryStore();

// Dynamic data state
const popularCategories = ref<Array<{ id: string; name: string; description: string; icon: string; count: number }>>([]);
const categoriesLoading = ref(true);
const appStats = ref({
  totalWallpapers: 0,
  totalCategories: 0,
  totalDownloads: 0
});
const statsLoading = ref(true);

// Category icons mapping
const categoryIcons: Record<string, string> = {
  'Nature': '🌿',
  'Abstract': '🎨',
  'Space': '🌌',
  'Architecture': '🏢',
  'Photography': '📸',
  'Technology': '💻',
  'Animals': '🐾',
  'Minimalist': '⚪',
  'Artistic': '🎭',
  'Gaming': '🎮',
  'Cars': '🏎️',
  'Anime': '⛩️',
  'Fantasy': '🧙',
  'Sci-Fi': '🚀',
  'Landscape': '🏞️',
  'Dark': '🌑',
  'Flowers': '🌸',
  'City': '🌃',
  'Ocean': '🌊',
  'Mountains': '⛰️',
  '3D Renders': '🧊',
  'Asphalt Legends': '🏁',
  'Uncategorized': '📁'
};

// Methods
const loadFeaturedWallpapers = async () => {
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({ limitCount: 24 });
    featuredWallpapers.value = result.wallpapers;
  } catch (error) {
    console.error('Error loading featured wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadUserFavorites = async () => {
  if (!currentUser.value) {
    userFavorites.value = [];
    return;
  }

  try {
    const favorites = await userService.getUserFavorites(currentUser.value.uid);
    userFavorites.value = favorites.map(fav => fav.wallpaperId);
  } catch (error) {
    console.error('Error loading user favorites:', error);
  }
};

const router = useRouter();

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};

// Dynamic data loading functions
const loadCategoryData = async () => {
  categoriesLoading.value = true;
  try {
    // Fetch all categories from the store
    await categoryStore.fetchCategories();

    // Get all categories with their counts
    const categories = categoryStore.categories;

    // Build categoryMap for WallpaperCard component
    categoryMap.value = categories.reduce((map, category) => {
      map[category.id] = category.name;
      return map;
    }, {} as Record<string, string>);

    // Transform categories for display
    const categoryData = categories.map(category => ({
      id: category.id,
      name: category.name,
      description: category.description,
      icon: categoryIcons[category.name] || '📷',
      count: category.wallpaperCount || 0
    }));

    // Sort by count and take top 5
    popularCategories.value = categoryData
      .filter(cat => cat.name !== 'Uncategorized')
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

  } catch (error) {
    console.error('Error loading category data:', error);
    // Fallback to default categories if error
    popularCategories.value = [
      { id: 'nature', name: 'Nature', description: 'Beautiful nature wallpapers', icon: '🌿', count: 0 },
      { id: 'abstract', name: 'Abstract', description: 'Abstract art and designs', icon: '🎨', count: 0 },
      { id: 'space', name: 'Space', description: 'Galaxies, planets, and stars', icon: '🌌', count: 0 },
      { id: 'architecture', name: 'Architecture', description: 'Stunning architectural designs', icon: '🏢', count: 0 },
      { id: 'photography', name: 'Photography', description: 'Professional photography', icon: '📸', count: 0 }
    ];
    
    // Build fallback categoryMap
    categoryMap.value = {
      'nature': 'Nature',
      'abstract': 'Abstract', 
      'space': 'Space',
      'architecture': 'Architecture',
      'photography': 'Photography'
    };
  } finally {
    categoriesLoading.value = false;
  }
};

const loadAppStats = async () => {
  statsLoading.value = true;
  try {
    // Get total wallpapers count
    const wallpapersResult = await wallpaperService.getWallpapers({ limitCount: 1000 });
    const totalWallpapers = wallpapersResult.wallpapers.length;

    // Get total categories count from the store
    await categoryStore.fetchCategories();
    const totalCategories = categoryStore.categories.length;

    // Calculate total downloads from all wallpapers
    const totalDownloads = wallpapersResult.wallpapers.reduce((sum, wallpaper) => {
      return sum + (wallpaper.downloadCount || 0);
    }, 0);

    appStats.value = {
      totalWallpapers,
      totalCategories,
      totalDownloads
    };

  } catch (error) {
    console.error('Error loading app stats:', error);
    // Keep default values on error
    appStats.value = {
      totalWallpapers: 0,
      totalCategories: 0,
      totalDownloads: 0
    };
  } finally {
    statsLoading.value = false;
  }
};

// Helper function to format numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M+`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K+`;
  } else if (num > 0) {
    return `${num}+`;
  }
  return '0';
};

// Lifecycle
onMounted(async () => {
  // Load featured wallpapers
  await loadFeaturedWallpapers();

  // Load dynamic data
  await Promise.all([
    loadCategoryData(),
    loadAppStats()
  ]);
});

watch(currentUser, async () => {
  await loadUserFavorites();
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(HeroSection),
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "section-header-left" }, [
            _createElementVNode("span", { class: "section-label" }, "Curated Collection"),
            _createElementVNode("h2", { class: "section-title" }, "Featured Wallpapers")
          ], -1)),
          _createVNode(_component_router_link, {
            to: "/categories",
            class: "view-all-link"
          }, {
            default: _withCtx(() => _cache[2] || (_cache[2] = [
              _createTextVNode(" View All ", -1),
              _createElementVNode("svg", {
                class: "view-all-arrow",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M17 8l4 4m0 0l-4 4m4-4H3"
                })
              ], -1)
            ])),
            _: 1,
            __: [2]
          })
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (viewMode.value = 'grid')),
              class: _normalizeClass(['view-toggle-btn', viewMode.value === 'grid' && 'active'])
            }, _cache[4] || (_cache[4] = [
              _createElementVNode("svg", {
                class: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                })
              ], -1),
              _createTextVNode(" Grid ", -1)
            ]), 2),
            _createElementVNode("button", {
              onClick: _cache[1] || (_cache[1] = ($event: any) => (viewMode.value = 'preview')),
              class: _normalizeClass(['view-toggle-btn', viewMode.value === 'preview' && 'active'])
            }, _cache[5] || (_cache[5] = [
              _createElementVNode("svg", {
                class: "w-4 h-4",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                })
              ], -1),
              _createTextVNode(" Preview ", -1)
            ]), 2)
          ])
        ]),
        _createVNode(WallpaperGrid, {
          wallpapers: featuredWallpapers.value,
          loading: loading.value,
          "has-more": false,
          "view-mode": viewMode.value,
          "category-map": categoryMap.value,
          onWallpaperClick: viewWallpaper
        }, null, 8, ["wallpapers", "loading", "view-mode", "category-map"])
      ])
    ]),
    _createVNode(SmartAd, {
      "ad-type": "banner",
      class: "ad-spacer",
      "min-content-height": 500
    }),
    _createElementVNode("section", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "section-header-left" }, [
            _createElementVNode("span", { class: "section-label" }, "Explore"),
            _createElementVNode("h2", { class: "section-title" }, "Popular Categories")
          ], -1)),
          _createVNode(_component_router_link, {
            to: "/categories",
            class: "view-all-link"
          }, {
            default: _withCtx(() => _cache[6] || (_cache[6] = [
              _createTextVNode(" See All ", -1),
              _createElementVNode("svg", {
                class: "view-all-arrow",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M17 8l4 4m0 0l-4 4m4-4H3"
                })
              ], -1)
            ])),
            _: 1,
            __: [6]
          })
        ]),
        (categoriesLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                return _createElementVNode("div", {
                  key: `skeleton-${n}`,
                  class: "category-card skeleton"
                }, _cache[8] || (_cache[8] = [
                  _createElementVNode("div", { class: "skeleton-icon" }, null, -1),
                  _createElementVNode("div", { class: "skeleton-text-lg" }, null, -1),
                  _createElementVNode("div", { class: "skeleton-text-sm" }, null, -1)
                ]))
              }), 64))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(popularCategories.value, (category) => {
                return (_openBlock(), _createBlock(_component_router_link, {
                  key: category.name,
                  to: `/categories?category=${category.name}`,
                  class: "category-card"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_12, _toDisplayString(category.icon), 1),
                    _createElementVNode("div", _hoisted_13, _toDisplayString(category.name), 1),
                    (category.description)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(category.description), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_15, _toDisplayString(formatNumber(category.count)) + " " + _toDisplayString(category.count === 1 ? 'wallpaper' : 'wallpapers'), 1)
                  ]),
                  _: 2
                }, 1032, ["to"]))
              }), 128))
            ]))
      ])
    ]),
    _createElementVNode("section", _hoisted_16, [
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "stat-icon" }, [
              _createElementVNode("svg", {
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "1.5",
                  d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                })
              ])
            ], -1)),
            (statsLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20))
              : (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(formatNumber(appStats.value.totalWallpapers)), 1)),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "stat-label" }, "Wallpapers", -1))
          ]),
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "stat-divider" }, null, -1)),
          _createElementVNode("div", _hoisted_22, [
            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "stat-icon" }, [
              _createElementVNode("svg", {
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "1.5",
                  d: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                })
              ])
            ], -1)),
            (statsLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_23))
              : (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(formatNumber(appStats.value.totalCategories)), 1)),
            _cache[12] || (_cache[12] = _createElementVNode("div", { class: "stat-label" }, "Categories", -1))
          ]),
          _cache[16] || (_cache[16] = _createElementVNode("div", { class: "stat-divider" }, null, -1)),
          _createElementVNode("div", _hoisted_25, [
            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "stat-icon" }, [
              _createElementVNode("svg", {
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "1.5",
                  d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                })
              ])
            ], -1)),
            (statsLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_26))
              : (_openBlock(), _createElementBlock("div", _hoisted_27, _toDisplayString(formatNumber(appStats.value.totalDownloads)), 1)),
            _cache[14] || (_cache[14] = _createElementVNode("div", { class: "stat-label" }, "Downloads", -1))
          ])
        ])
      ])
    ]),
    _createVNode(SmartAd, {
      "ad-type": "rectangle",
      class: "ad-spacer ad-center",
      "min-content-height": 1000
    })
  ]))
}
}

})